import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent, DiscountUpdateEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

/** @type {number} Delay before first verification attempt (ms) */
const VERIFICATION_INITIAL_DELAY = 600;

/** @type {number} Delay between verification retries (ms) */
const VERIFICATION_RETRY_DELAY = 500;

/** @type {number} Maximum verification attempts */
const VERIFICATION_MAX_RETRIES = 3;

/**
 * Global lock to prevent multiple instances from adding gifts simultaneously.
 * This prevents race conditions when cart-drawer and cart-page both have gift components.
 * Uses window object to share lock across different gift component modules.
 */
const getGiftLock = () => window.__giftAddingLock || false;
const setGiftLock = (value) => { window.__giftAddingLock = value; };

/**
 * Component that automatically adds a product when cart reaches a specified threshold.
 * Monitors cart updates and adds the configured product when threshold is reached.
 *
 * @extends {Component}
 */
class CartThresholdAutoAddComponent extends Component {
  /** @type {AbortController | null} */
  #activeRequest = null;
  
  /** @type {boolean} */
  #isUpdating = false;

  /** @type {string | null} - Product handle to add */
  #productHandle = null;

  /** @type {number | null} - Threshold in cents */
  #threshold = null;

  /** @type {string | null} - Variant ID of the product to add */
  #variantId = null;

  /** @type {string | null} - Optional discount code that must be applied */
  #discountCode = null;

  /** @type {string | null} - Discount gift product handle */
  #discountGiftHandle = null;

  /** @type {string | null} - Discount gift variant ID */
  #discountGiftVariantId = null;

  constructor() {
    super();
    this.hasAutoAddedProduct = false;
    this.hasDiscountGift = false;
  }

  connectedCallback() {
    super.connectedCallback();
    
    // Get variant ID from data attribute or find it via product handle
    this.#variantId = this.dataset.variantId || null;
    const thresholdValue = this.dataset.threshold;
    this.#threshold = thresholdValue ? parseInt(thresholdValue, 10) : null;
    this.#productHandle = this.dataset.productHandle || null;
    this.#discountCode = this.dataset.discountCode || null;
    this.#discountGiftHandle = this.dataset.discountGiftHandle || null;
    this.#discountGiftVariantId = this.dataset.discountGiftVariantId || null;
    
    // Check if product was already auto-added
    this.hasAutoAddedProduct = this.hasAttribute('data-has-auto-product');
    this.hasDiscountGift = this.hasAttribute('data-has-discount-gift');
    
    // console.log('🛒 Cart threshold component initialized:', {
    //   threshold: this.#threshold,
    //   productHandle: this.#productHandle,
    //   variantId: this.#variantId,
    //   hasAutoAddedProduct: this.hasAutoAddedProduct,
    //   dataset: this.dataset
    // });
    
    // Listen for cart updates (including discount updates)
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.#handleCartUpdate);
    // console.log('🛒 Listening for cart update events');
    
    // Debug: Listen for all events to see what's firing
    // ['cart:update', 'cart:error', 'variant:update', 'variant:selected'].forEach(eventName => {
    //   document.addEventListener(eventName, (e) => {
    //     console.log(`🛒 Event detected: ${eventName}`, e.detail);
    //   });
    // });
    
    // Validate required configuration
    if (!this.#threshold) {
      console.error('🛒 Cart threshold component: Missing threshold configuration. Please configure in theme settings.');
      return;
    }

    if (!this.#productHandle && !this.#variantId) {
      console.error('🛒 Cart threshold component: Missing product configuration. Please configure product in theme settings.');
      return;
    }

    // If no variant ID provided, fetch it
    if (!this.#variantId && this.#productHandle) {
      // console.log('🛒 No variant ID provided, fetching from product handle...');
      this.#fetchVariantId();
    }
    
    // If no discount gift variant ID provided, fetch it
    if (!this.#discountGiftVariantId && this.#discountGiftHandle) {
      this.#fetchDiscountGiftVariantId();
    }
    
    // Check threshold on initial load (in case cart already has items)
    setTimeout(() => {
      // console.log('🛒 Checking threshold on initial load...');
      this.#checkThresholdWithFreshCartData();
    }, 100);
    
    // Check discount gift on initial load
    setTimeout(() => {
      this.#checkDiscountGiftWithFreshCartData();
    }, 150);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.#handleCartUpdate);
    this.#activeRequest?.abort();
  }

  /**
   * Fetches the variant ID for the discount gift product handle
   */
  async #fetchDiscountGiftVariantId() {
    try {
      const response = await fetch(`/products/${this.#discountGiftHandle}.js`);
      
      if (response.ok) {
        const product = await response.json();
        
        if (product.variants && product.variants.length > 0) {
          this.#discountGiftVariantId = product.variants[0].id.toString();
          this.#checkDiscountGiftWithFreshCartData();
        } else {
          console.error('🛒 No variants found for discount gift product');
        }
      } else {
        console.error('🛒 Failed to fetch discount gift product:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('🛒 Failed to fetch discount gift product variant:', error);
    }
  }

  /**
   * Fetches the variant ID for the product handle
   */
  async #fetchVariantId() {
    try {
      // console.log('🛒 Fetching variant ID for product:', this.#productHandle);
      const response = await fetch(`/products/${this.#productHandle}.js`);
      // console.log('🛒 Product fetch response:', response.status, response.ok);
      
      if (response.ok) {
        const product = await response.json();
        // console.log('🛒 Product data:', product);
        
        if (product.variants && product.variants.length > 0) {
          this.#variantId = product.variants[0].id.toString();
          // console.log('🛒 Found variant ID:', this.#variantId);
          
          // Now that we have the variant ID, check the cart threshold
          // console.log('🛒 Variant ID fetched, checking initial cart state...');
          this.#checkThresholdWithFreshCartData();
        } else {
          console.error('🛒 No variants found for product');
        }
      } else {
        console.error('🛒 Failed to fetch product:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('🛒 Failed to fetch product variant:', error);
    }
  }

  /**
   * Handles cart update events to check threshold
   * @param {Event} event - Cart update event or discount update event
   */
  #handleCartUpdate = async (event) => {
    // Handle both CartUpdateEvent and DiscountUpdateEvent
    if (event instanceof CartUpdateEvent) {
      // Don't react to our own updates
      if (event.detail.data?.source === 'cart-threshold-auto-add') {
        // console.log('🛒 Ignoring our own update event');
        return;
      }
    }
    
    // console.log('🛒 Cart threshold component - Cart update event received:', {
    //   eventSource: event instanceof CartUpdateEvent ? event.detail.data?.source : 'discount-update',
    //   eventType: event.type
    // });
    
    // For discount updates, add a small delay to ensure cart is fully updated
    if (event instanceof DiscountUpdateEvent) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    // Always fetch fresh cart data to get current state
    await this.#checkThresholdWithFreshCartData();
    
    // Also check discount gift on cart updates
    await this.#checkDiscountGiftWithFreshCartData();
  };

  /**
   * Checks if the required discount code is applied to the cart
   * @param {any} cartData - Cart data from /cart.js
   * @returns {boolean} True if discount code requirement is met
   */
  #checkDiscountCode(cartData) {
    // If no discount code is configured, requirement is always met
    if (!this.#discountCode) {
      return true;
    }

    // Check if discount code is in cart's discount_codes array AND is applicable
    // When codes are replaced, old codes remain but with applicable: false
    const discountCodes = cartData.discount_codes || [];
    const discountCode = this.#discountCode;
    
    return discountCodes.some((/** @type {any} */ discount) => {
      return discount.code && 
             discount.code.toLowerCase() === discountCode.toLowerCase() &&
             discount.applicable === true;
    });
  }

  /**
   * Fetches fresh cart data and checks threshold
   */
  async #checkThresholdWithFreshCartData() {
    if (this.#isUpdating || !this.#threshold) {
      // console.log('🛒 Already updating or threshold not configured, skipping threshold check');
      return;
    }

    try {
      // console.log('🛒 Fetching fresh cart data...');
      const response = await fetch('/cart.js');
      if (!response.ok) return;
      const cartData = await response.json();
      
      // console.log('🛒 Fresh cart data received:', {
      //   totalPrice: cartData.total_price,
      //   itemCount: cartData.item_count,
      //   items: cartData.items?.length || 0
      // });

      const cartTotal = cartData.total_price || 0;
      const hasAutoProductInCart = (cartData.items || []).some((/** @type {any} */ item) => 
        item.properties && item.properties._auto_added_threshold === 'true'
      ) || false;

      const hasDiscountGiftInCart = (cartData.items || []).some((/** @type {any} */ item) => 
        item.properties && item.properties._freegifincart === 'true'
      ) || false;

      // Check if required discount code is applied (if configured)
      const hasRequiredDiscountCode = this.#checkDiscountCode(cartData);

      // Calculate cart total excluding auto-added products for threshold check
      let cartTotalExcludingAuto = cartTotal;
      if (hasAutoProductInCart && cartData.items) {
        const autoAddedItems = (cartData.items || []).filter((/** @type {any} */ item) => 
          item.properties && item.properties._auto_added_threshold === 'true'
        );
        const autoAddedTotal = autoAddedItems.reduce(
          /** @type {(sum: number, item: any) => number} */
          ((sum, item) => {
            const linePrice = item.final_line_price || 0;
            return sum + linePrice;
          }),
          0
        );
        cartTotalExcludingAuto = cartTotal - autoAddedTotal;
      }

      // console.log('🛒 Fresh threshold check:', {
      //   cartTotal,
      //   cartTotalDollars: (cartTotal / 100).toFixed(2),
      //   threshold: this.#threshold,
      //   thresholdDollars: (this.#threshold / 100).toFixed(2),
      //   hasAutoProductInCart: hasAutoProductInCart,
      //   hasRequiredDiscountCode: hasRequiredDiscountCode,
      //   discountCode: this.#discountCode,
      //   variantId: this.#variantId,
      //   isUpdating: this.#isUpdating,
      //   shouldAdd: cartTotalExcludingAuto >= this.#threshold && !hasAutoProductInCart && hasRequiredDiscountCode && this.#variantId && !this.#isUpdating,
      //   shouldRemove: (cartTotalExcludingAuto < this.#threshold || !hasRequiredDiscountCode) && hasAutoProductInCart && !this.#isUpdating
      // });

      // Update component state to match current cart
      this.hasAutoAddedProduct = hasAutoProductInCart;
      this.hasDiscountGift = hasDiscountGiftInCart;
      if (hasAutoProductInCart) {
        this.setAttribute('data-has-auto-product', 'true');
      } else {
        this.removeAttribute('data-has-auto-product');
      }
      if (hasDiscountGiftInCart) {
        this.setAttribute('data-has-discount-gift', 'true');
      } else {
        this.removeAttribute('data-has-discount-gift');
      }

      // Check if we should add the product (when reaching threshold AND discount code requirement met)
      const shouldAdd = cartTotalExcludingAuto >= this.#threshold && 
                       !hasAutoProductInCart && 
                       hasRequiredDiscountCode && 
                       this.#variantId && 
                       !this.#isUpdating;
      
      if (shouldAdd) {
        // console.log('🛒 Adding threshold product - cart reached threshold and discount code requirement!');
        this.#addThresholdProduct();
      }
      // Check if we should remove the product (if total drops below threshold OR discount code removed)
      else {
        const shouldRemove = hasAutoProductInCart && 
                             !this.#isUpdating && 
                             (cartTotalExcludingAuto < this.#threshold || !hasRequiredDiscountCode);
        
        if (shouldRemove) {
          // console.log('🛒 Removing threshold product - cart dropped below threshold or discount code removed!');
          this.#removeThresholdProduct();
        }
      }
      
      // Check discount gift separately
      this.#checkDiscountGiftWithFreshCartData(cartData, hasRequiredDiscountCode, hasDiscountGiftInCart);
    } catch (error) {
      console.error('🛒 Failed to fetch fresh cart data:', error);
    }
  };

  /**
   * Adds the threshold product to cart
   */
  async #addThresholdProduct() {
    if (this.#isUpdating || !this.#variantId || !this.#threshold) return;

    // Global lock to prevent multiple instances from adding simultaneously
    if (getGiftLock()) {
      console.log('🛒 [Threshold] Another instance is adding gift, skipping');
      return;
    }

    // Check if the gift variant is already in cart (as any line item)
    // This prevents infinite loops when gift is converted to regular product
    try {
      const checkResponse = await fetch('/cart.js');
      if (!checkResponse.ok) return;
      const checkCart = await checkResponse.json();
      const giftVariantAlreadyInCart = (checkCart.items || []).some(
        (/** @type {any} */ item) => item.variant_id.toString() === this.#variantId
      );

      if (giftVariantAlreadyInCart) {
        console.log('🛒 [Threshold] Gift variant already in cart, skipping add');
        return;
      }
    } catch (error) {
      console.error('🛒 [Threshold] Error checking cart for existing variant:', error);
    }

    // Set global lock before adding
    setGiftLock(true);
    this.#setLoadingState(true);

    try {
      // Add the product to cart with special properties
      const formData = new FormData();
      formData.append('id', this.#variantId);
      formData.append('quantity', '1');
      formData.append('properties[_auto_added_threshold]', 'true');
      formData.append('properties[_free_gift]', 'true');
      formData.append('properties[_threshold_amount]', (this.#threshold / 100).toString());
      formData.append('sections', this.dataset.sectionId || '');

      const response = await fetch('/cart/add.js', {
        ...fetchConfig('javascript', { body: formData }),
        signal: this.#createAbortController().signal,
      });

      if (!response.ok) {
        throw new Error('Failed to add threshold product');
      }

      const data = await response.json();

      // Update the cart drawer/section with new content
      const sectionId = this.dataset.sectionId;
      if (data.sections && sectionId && data.sections[sectionId]) {
        morphSection(sectionId, data.sections[sectionId]);
      }

      // Dispatch cart update event
      this.dispatchEvent(new CartUpdateEvent(
        data,
        this.id,
        {
          source: 'cart-threshold-auto-add',
          itemCount: data.item_count,
        }
      ));

      // Verify that the automatic discount was applied to the gift
      // Shopify automatic discounts apply asynchronously
      this.#setLoadingState(false); // Temporarily release loading state for verification
      const discountApplied = await this.#verifyGiftDiscount('_auto_added_threshold');

      if (!discountApplied) {
        console.warn('🛒 [Threshold] Discount not applied - converting to regular product');
        await this.#convertGiftToRegularProduct('_auto_added_threshold');
        return;
      }

      // Update component state only after successful verification
      this.hasAutoAddedProduct = true;
      this.setAttribute('data-has-auto-product', 'true');
      // console.log('🛒 Updated component state: hasAutoAddedProduct = true');

    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Cart threshold auto-add error:', error);
      }
    } finally {
      this.#setLoadingState(false);
      // Release global lock
      setGiftLock(false);
    }
  }

  /**
   * Checks and manages discount gift product based on discount code condition
   * @param {any} cartData - Cart data from /cart.js
   * @param {boolean | null} hasRequiredDiscountCode - Whether the required discount code is applied
   * @param {boolean | null} hasDiscountGiftInCart - Whether discount gift is already in cart
   */
  async #checkDiscountGiftWithFreshCartData(cartData = null, hasRequiredDiscountCode = null, hasDiscountGiftInCart = null) {
    // Only proceed if discount code is configured (required condition)
    if (!this.#discountCode) {
      return;
    }

    try {
      // Always fetch fresh cart data to ensure we have the latest discount codes
      // This is critical when discount codes are being replaced
      const response = await fetch('/cart.js');
      if (!response.ok) return;
      cartData = await response.json();

      // Always recalculate from fresh cart data - don't trust passed parameters
      // This ensures we catch when the configured discount code is replaced with a different one
      hasRequiredDiscountCode = this.#checkDiscountCode(cartData);

      // Check if discount gift is in cart
      hasDiscountGiftInCart = (cartData.items || []).some((/** @type {any} */ item) => 
        item.properties && item.properties._freegifincart === 'true'
      ) || false;

      const cartHasCustomerItems = (cartData.items || []).some((/** @type {any} */ item) =>
        !item.properties || (item.properties._auto_added_threshold !== 'true' && item.properties._freegifincart !== 'true')
      );

      // Add discount gift if: discount code is set AND configured code is applied AND cart has customer items AND gift not already in cart
      const shouldAddDiscountGift = this.#discountCode && 
                                    hasRequiredDiscountCode && 
                                    cartHasCustomerItems && 
                                    !hasDiscountGiftInCart && 
                                    this.#discountGiftVariantId && 
                                    !this.#isUpdating;

      // Remove discount gift if: configured discount code is NOT applied (removed or replaced) OR cart becomes empty OR no customer items
      // This handles both removal and replacement scenarios
      const shouldRemoveDiscountGift = hasDiscountGiftInCart && 
                                       !this.#isUpdating && 
                                       (!hasRequiredDiscountCode || !cartHasCustomerItems);

      if (shouldAddDiscountGift) {
        this.#addDiscountGift();
      } else if (shouldRemoveDiscountGift) {
        this.#removeDiscountGift();
      }
    } catch (error) {
      console.error('🛒 Failed to check discount gift:', error);
    }
  }

  /**
   * Adds the discount gift product to cart
   */
  async #addDiscountGift() {
    if (this.#isUpdating || !this.#discountGiftVariantId || !this.#discountCode) return;

    // Global lock to prevent multiple instances from adding simultaneously
    if (getGiftLock()) {
      console.log('🛒 [Discount Gift] Another instance is adding gift, skipping');
      return;
    }

    // Check if the gift variant is already in cart (as any line item)
    // This prevents infinite loops when gift is converted to regular product
    try {
      const checkResponse = await fetch('/cart.js');
      if (!checkResponse.ok) return;
      const checkCart = await checkResponse.json();
      const giftVariantAlreadyInCart = (checkCart.items || []).some(
        (/** @type {any} */ item) => item.variant_id.toString() === this.#discountGiftVariantId
      );

      if (giftVariantAlreadyInCart) {
        console.log('🛒 [Discount Gift] Gift variant already in cart, skipping add');
        return;
      }
    } catch (error) {
      console.error('🛒 [Discount Gift] Error checking cart for existing variant:', error);
    }

    // Set global lock before adding
    setGiftLock(true);
    this.#setLoadingState(true);

    try {
      const formData = new FormData();
      formData.append('id', this.#discountGiftVariantId);
      formData.append('quantity', '1');
      formData.append('properties[_freegifincart]', 'true');
      formData.append('sections', this.dataset.sectionId || '');

      const response = await fetch('/cart/add.js', {
        ...fetchConfig('javascript', { body: formData }),
        signal: this.#createAbortController().signal,
      });

      if (!response.ok) {
        throw new Error('Failed to add discount gift product');
      }

      const data = await response.json();

      const sectionId = this.dataset.sectionId;
      if (data.sections && sectionId && data.sections[sectionId]) {
        morphSection(sectionId, data.sections[sectionId]);
      }

      this.dispatchEvent(new CartUpdateEvent(
        data,
        this.id,
        {
          source: 'cart-threshold-auto-add',
          itemCount: data.item_count,
        }
      ));

      // Verify that the automatic discount was applied to the gift
      // Shopify automatic discounts apply asynchronously
      this.#setLoadingState(false); // Temporarily release loading state for verification
      const discountApplied = await this.#verifyGiftDiscount('_freegifincart');

      if (!discountApplied) {
        console.warn('🛒 [Discount Gift] Discount not applied - converting to regular product');
        await this.#convertGiftToRegularProduct('_freegifincart');
        return;
      }

      // Update component state only after successful verification
      this.hasDiscountGift = true;
      this.setAttribute('data-has-discount-gift', 'true');

    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Cart discount gift auto-add error:', error);
      }
    } finally {
      this.#setLoadingState(false);
      // Release global lock
      setGiftLock(false);
    }
  }

  /**
   * Removes the discount gift product from cart
   */
  async #removeDiscountGift() {
    if (this.#isUpdating) return;
    
    this.#setLoadingState(true);
    
    try {
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) return;
      const cart = await cartResponse.json();

      const discountGiftItem = (cart.items || []).find((/** @type {any} */ item) =>
        item.properties && item.properties._freegifincart === 'true'
      );
      
      if (discountGiftItem) {
        const removeData = JSON.stringify({
          id: discountGiftItem.key,
          quantity: 0,
          sections: this.dataset.sectionId,
        });
        
        const response = await fetch('/cart/change.js', {
          ...fetchConfig('json', { body: removeData }),
          signal: this.#createAbortController().signal,
        });
        
        if (response.ok) {
          const data = await response.json();
          
          const sectionId = this.dataset.sectionId;
          if (data.sections && sectionId && data.sections[sectionId]) {
            morphSection(sectionId, data.sections[sectionId]);
          }
          
          this.dispatchEvent(new CartUpdateEvent(
            data,
            this.id,
            {
              source: 'cart-threshold-auto-add',
              itemCount: data.item_count,
            }
          ));
        }
      }
      
      this.hasDiscountGift = false;
      this.removeAttribute('data-has-discount-gift');
      
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Failed to remove discount gift product:', error);
      }
    } finally {
      this.#setLoadingState(false);
    }
  }

  /**
   * Removes the threshold product from cart when total drops below threshold
   */
  async #removeThresholdProduct() {
    if (this.#isUpdating) return;
    
    this.#setLoadingState(true);
    
    try {
      // Get current cart to find the auto-added item
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) return;
      const cart = await cartResponse.json();

      // Find the auto-added threshold product
      /** @type {any} */
      const autoAddedItem = (cart.items || []).find((/** @type {any} */ item) => 
        item.properties && item.properties._auto_added_threshold === 'true'
      );
      
      if (autoAddedItem) {
        const removeData = JSON.stringify({
          id: autoAddedItem.key,
          quantity: 0,
          sections: this.dataset.sectionId,
        });
        
        const response = await fetch('/cart/change.js', {
          ...fetchConfig('json', { body: removeData }),
          signal: this.#createAbortController().signal,
        });
        
        if (response.ok) {
          const data = await response.json();
          
          // Update the cart drawer
          const sectionId = this.dataset.sectionId;
          if (data.sections && sectionId && data.sections[sectionId]) {
            morphSection(sectionId, data.sections[sectionId]);
          }
          
          // Dispatch update event
          this.dispatchEvent(new CartUpdateEvent(
            data,
            this.id,
            {
              source: 'cart-threshold-auto-add',
              itemCount: data.item_count,
            }
          ));
        }
      }
      
      // Update component state to match cart
      this.hasAutoAddedProduct = false;
      this.removeAttribute('data-has-auto-product');
      // console.log('🛒 Updated component state: hasAutoAddedProduct = false');
      
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Failed to remove threshold product:', error);
      }
    } finally {
      this.#setLoadingState(false);
    }
  }

  /**
   * Creates and manages abort controller
   * @returns {AbortController}
   */
  #createAbortController() {
    this.#activeRequest?.abort();
    this.#activeRequest = new AbortController();
    return this.#activeRequest;
  }

  /**
   * Sets loading state
   * @param {boolean} loading - Loading state
   */
  #setLoadingState(loading) {
    this.#isUpdating = loading;

    // Could add loading indicators here if needed
    if (loading) {
      this.classList.add('cart-threshold--loading');
    } else {
      this.classList.remove('cart-threshold--loading');
    }
  }

  /**
   * Converts a gift item to a regular product by removing it and re-adding without gift properties.
   * This allows customers to keep the product at full price if they want.
   * @param {string} propertyKey - The property key to identify the gift ('_auto_added_threshold' or '_freegifincart')
   */
  async #convertGiftToRegularProduct(propertyKey) {
    try {
      // 1. Get cart and find gift item
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) return;
      const cart = await cartResponse.json();
      const giftItem = (cart.items || []).find((/** @type {any} */ item) =>
        item.properties && item.properties[propertyKey] === 'true'
      );

      if (!giftItem) {
        console.log('🛒 Gift item not found for conversion');
        return;
      }

      const variantId = giftItem.variant_id;

      // 2. Remove the gift item (with special properties)
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: giftItem.key, quantity: 0 })
      });

      // 3. Re-add as regular product (no gift properties)
      const formData = new FormData();
      formData.append('id', variantId.toString());
      formData.append('quantity', '1');
      formData.append('sections', this.dataset.sectionId || '');

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      // 4. Update UI
      const sectionId = this.dataset.sectionId;
      if (data.sections && sectionId && data.sections[sectionId]) {
        morphSection(sectionId, data.sections[sectionId]);
      }

      // Dispatch cart update event
      this.dispatchEvent(new CartUpdateEvent(
        data,
        this.id,
        {
          source: 'cart-threshold-auto-add',
          itemCount: data.item_count,
        }
      ));

      console.log('🛒 Converted gift to regular product - customer can remove if desired');
    } catch (error) {
      console.error('🛒 Error converting gift to regular product:', error);
    }
  }

  /**
   * Verifies that the automatic discount was applied to the gift product.
   * Shopify automatic discounts apply asynchronously, so we retry multiple times.
   * @param {string} propertyKey - The property key to identify the gift ('_auto_added_threshold' or '_freegifincart')
   * @returns {Promise<boolean>} True if discount verified (final_line_price is 0 or has discount allocations), false otherwise
   */
  async #verifyGiftDiscount(propertyKey) {
    // Initial delay before first check
    await new Promise(resolve => setTimeout(resolve, VERIFICATION_INITIAL_DELAY));

    for (let attempt = 0; attempt < VERIFICATION_MAX_RETRIES; attempt++) {
      try {
        const cartResponse = await fetch('/cart.js');
        if (!cartResponse.ok) continue;
        const cartData = await cartResponse.json();

        const giftItem = (cartData.items || []).find((/** @type {any} */ item) =>
          item.properties && item.properties[propertyKey] === 'true'
        );

        if (!giftItem) {
          // Gift was removed externally
          return false;
        }

        // Check if discount is applied:
        // 1. final_line_price should be 0 (completely free)
        // 2. OR has line_level_discount_allocations (has some discount)
        if (giftItem.final_line_price === 0) {
          return true;
        }

        if (giftItem.line_level_discount_allocations &&
            giftItem.line_level_discount_allocations.length > 0) {
          return true;
        }

        // Discount not applied yet, wait and retry
        if (attempt < VERIFICATION_MAX_RETRIES - 1) {
          await new Promise(resolve => setTimeout(resolve, VERIFICATION_RETRY_DELAY));
        }
      } catch (error) {
        console.error('🛒 Error verifying gift discount:', error);
        return false;
      }
    }

    // Discount never applied after all retries
    return false;
  }
}

if (!customElements.get('cart-threshold-auto-add')) {
  customElements.define('cart-threshold-auto-add', CartThresholdAutoAddComponent);
}

/**
 * Cleanup component that removes gift products when the feature is disabled.
 * This is rendered by the Liquid template when feature is off but gifts exist in cart.
 */
class CartThresholdCleanupComponent extends HTMLElement {
  connectedCallback() {
    this.#cleanup();
  }

  async #cleanup() {
    const cleanupAutoProduct = this.dataset.cleanupAutoProduct === 'true';
    const cleanupDiscountGift = this.dataset.cleanupDiscountGift === 'true';
    const sectionId = this.dataset.sectionId;

    if (!cleanupAutoProduct && !cleanupDiscountGift) {
      return;
    }

    try {
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) return;
      const cart = await cartResponse.json();

      const itemsToRemove = [];

      for (const item of cart.items) {
        if (cleanupAutoProduct && item.properties?._auto_added_threshold === 'true') {
          itemsToRemove.push(item.key);
        }
        if (cleanupDiscountGift && item.properties?._freegifincart === 'true') {
          itemsToRemove.push(item.key);
        }
      }

      // Remove items one by one
      for (const key of itemsToRemove) {
        const removeData = JSON.stringify({
          id: key,
          quantity: 0,
          sections: sectionId,
        });

        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: removeData,
        });

        if (response.ok) {
          const data = await response.json();

          // Update cart section if available
          if (data.sections && sectionId && data.sections[sectionId]) {
            const { morphSection } = await import('@theme/section-renderer');
            morphSection(sectionId, data.sections[sectionId]);
          }
        }
      }

      if (itemsToRemove.length > 0) {
        console.log('🛒 Cart threshold cleanup: Removed', itemsToRemove.length, 'gift product(s) - feature is disabled');
      }
    } catch (error) {
      console.error('🛒 Failed to cleanup gift products:', error);
    }
  }
}

if (!customElements.get('cart-threshold-cleanup')) {
  customElements.define('cart-threshold-cleanup', CartThresholdCleanupComponent);
}
